import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Environment } from "../../environment";
import { catchError, map, of, tap } from "rxjs";
import { Router } from "@angular/router";
import { LoginPageComponent } from "../pages/login-page/login-page.component";

export interface AuthResponse {
  token: string;
  email: string;
}

export interface TokenValidationResponse {
  isValid: boolean;
  message: string;
  token?: string;
}

export interface OpenAiApiKeyResponse {
  apiKey: string;
  email: string;
  openaiBaseUrl: string;
  anthropicBaseUrl: string;
}

@Injectable({
  providedIn: "root",
})
export class AccountService {
  private readonly accountsUri = `${Environment.ApiUri}/account`;
  private readonly authStorageKey = "chatbot.authResponse";

  private authResponse?: AuthResponse;

  public constructor(private httpClient: HttpClient, private router: Router) {
    this.authResponse = this.restoreAuthResponse();
  }

  public get authToken() {
    return this.authResponse?.token;
  }

  public get authEmail() {
    return this.authResponse?.email;
  }

  public get isLoggedIn$() {
    return this.validateToken$().pipe(
      map((response) => response.isValid),
    );
  }

  public logout() {
    this.setAuthResponse(undefined);
    this.router.navigateByUrl(LoginPageComponent.Path);
  }

  public validateToken$() {
    if (!this.authResponse) {
      return of({ isValid: false, message: "Not logged in" } as TokenValidationResponse);
    } else {
      return this.httpClient.post<TokenValidationResponse>(`${this.accountsUri}/validate-token`, { token: this.authResponse.token }).pipe(
        tap((response) => {
          if (response.isValid && response.token && this.authResponse) {
            this.setAuthResponse({ ...this.authResponse, token: response.token });
          } else {
            this.setAuthResponse(undefined);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          console.error("Token validation failed", error);
          this.setAuthResponse(undefined);
          return of({ isValid: false, message: "Token validation failed" } as TokenValidationResponse);
        }),
      );
    }
  }

  public registerNewAccount(email: string, password: string) {
    return this.httpClient.post<AuthResponse>(`${this.accountsUri}/register`, { email, password }).pipe(
      tap((response) => this.setAuthResponse(response)),
    );
  }

  public login(email: string, password: string) {
    return this.httpClient.post<AuthResponse>(`${this.accountsUri}/login`, { email, password }).pipe(
      tap((response) => this.setAuthResponse(response)),
    );
  }

  public getOpenAiApiKey() {
    return this.httpClient.get<OpenAiApiKeyResponse>(`${this.accountsUri}/openai-api-key`, {
      headers: {
        Authorization: `Bearer ${this.authToken!}`,
      },
    });
  }

  private setAuthResponse(authResponse?: AuthResponse) {
    this.authResponse = authResponse;

    if (typeof localStorage === "undefined") {
      return;
    }

    if (authResponse) {
      localStorage.setItem(this.authStorageKey, JSON.stringify(authResponse));
    } else {
      localStorage.removeItem(this.authStorageKey);
    }
  }

  private restoreAuthResponse() {
    if (typeof localStorage === "undefined") {
      return undefined;
    }

    const storedAuthResponse = localStorage.getItem(this.authStorageKey);
    if (!storedAuthResponse) {
      return undefined;
    }

    try {
      return JSON.parse(storedAuthResponse) as AuthResponse;
    } catch {
      localStorage.removeItem(this.authStorageKey);
      return undefined;
    }
  }
}
